<script lang="ts">
  import { page, Link } from '@inertiajs/svelte'
  import Library from '@lucide/svelte/icons/library'
  import BookOpen from '@lucide/svelte/icons/book-open'
  import Mail from '@lucide/svelte/icons/mail'
  import Trophy from '@lucide/svelte/icons/trophy'
  import Home from '@lucide/svelte/icons/home'
  import { inboxCount } from '@/stores/cable'
  import { preloadOnIntent, type PageIntentEvent } from '@/utils/pageIntent'
  import type { PreloadablePage } from '@/pages'
  import {
    books_path,
    profile_path,
    inbox_path,
    root_path,
    awards_path,
  } from '@/routes'

  let currentUser = $derived(page.props.current_user)
  let currentPath = $derived(page.url)

  type NavItem = {
    href: string
    label: string
    icon: typeof Home
    exactMatch?: boolean
    showBadge?: boolean
    page?: PreloadablePage
  }

  let navItems = $derived<NavItem[]>([
    { href: root_path(), label: 'Activity', icon: Home, exactMatch: true },
    { href: awards_path(), label: 'Awards', icon: Trophy },
    {
      href: profile_path(currentUser?.username ?? ''),
      label: 'Reading',
      icon: Library,
      page: 'profiles/show',
    },
    { href: books_path(), label: 'Books', icon: BookOpen },
    { href: inbox_path(), label: 'Inbox', icon: Mail, showBadge: true },
  ])

  function isActive(path: string, exactMatch: boolean = false): boolean {
    return exactMatch ? currentPath === path : currentPath.startsWith(path)
  }

  function getLinkClass(path: string, exactMatch: boolean = false): string {
    const active = isActive(path, exactMatch)
    const baseClasses =
      'flex flex-col items-center justify-center flex-1 py-2 transition-colors relative'

    return active
      ? `${baseClasses} text-neutral-900 dark:text-white`
      : `${baseClasses} text-neutral-500 dark:text-gray-400`
  }
</script>

{#if currentUser}
  <nav
    aria-label="Primary mobile navigation"
    class="md:hidden w-full shrink-0 bg-white dark:bg-gray-800 border-t border-neutral-200 dark:border-gray-700 z-50 safe-area-inset-bottom"
  >
    <div class="flex justify-around items-center h-16 px-2">
      {#each navItems as item (item.href)}
        <Link
          href={item.href}
          onpointerdown={(event: PageIntentEvent) => {
            if (item.page) preloadOnIntent(item.page, event)
          }}
          onfocus={() => {
            if (item.page) preloadOnIntent(item.page)
          }}
          prefetch={item.exactMatch ? 'mount' : false}
          cacheFor={item.exactMatch ? ['5s', '30s'] : 0}
          class={getLinkClass(item.href, item.exactMatch)}
        >
          {#if isActive(item.href, item.exactMatch)}
            <div
              class="absolute bottom-0 left-0 right-0 h-0.5 bg-neutral-900 dark:bg-white"
            ></div>
          {/if}
          {#if item.showBadge}
            <div class="relative">
              <item.icon size={24} />
              {#if $inboxCount > 0}
                <span
                  class="absolute -top-1 -right-1 bg-danger-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                >
                  {$inboxCount > 9 ? '9+' : $inboxCount}
                </span>
              {/if}
            </div>
          {:else}
            <item.icon size={24} />
          {/if}
          <span class="text-xs mt-1 font-medium">{item.label}</span>
        </Link>
      {/each}
    </div>
  </nav>
{/if}

<style>
  /* Handle safe area for devices with notches/home indicators */
  .safe-area-inset-bottom {
    padding-bottom: env(safe-area-inset-bottom);
  }
</style>
